import * as z from 'zod'

import { AssetPriceSource } from '@/entities/assets/components/utils/enums'

import { ACCOUNT_MODE_FIELD } from './const'
import { WizardAccountMode, WizardInvestor, WizardValues } from './types'

/**
 * The pieces every wizard schema is built from.
 *
 * Form state is strings, with one exception: an amount is a number, because
 * DSAmountField holds one and every reader of an amount — the schema, the
 * summary, whatever posts the transaction — wants a number rather than a
 * string it has to convert. Empty is `undefined` there, not `''`.
 *
 * Nothing here coerces. A schema that accepted `'1,000'` and quietly turned it
 * into 1000 would be doing the control's job with less information about what
 * the user actually typed.
 */

export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** A field the user must supply. The message is what the field shows when empty. */
export const requiredText = (message: string) => z.string().trim().min(1, message)

/** A field that may be left empty. Absent and empty are the same thing here. */
export const optionalText = () => z.string().trim().optional()

/**
 * An amount: a number, and not zero. The *sign* is deliberately not checked
 * here.
 *
 * Which way an amount runs belongs to the repository's transaction settings —
 * a purchase cost is `maxzero`, a liability balance `minzero`, funding
 * `negative` — and a schema is a static module that cannot read them. Demanding
 * `> 0`, as this once did, contradicted the settings for the commonest case in
 * these wizards.
 *
 * Nothing in the wizard holds the user to a sign: the field collects a
 * magnitude and `pasteAmount` applies the rule when the record is written. The
 * control was bounded by that rule for a while, and it cost the user their
 * value — see WizardControl.
 *
 * `message` covers the empty field as well as a non-number: `undefined` is
 * what an amount holds until one is entered, and "Enter the purchase price" is
 * the right thing to say about both.
 */
export const requiredAmount = (message: string) =>
  z
    .number({ error: message })
    .refine(value => value !== 0, 'Enter an amount other than zero')

export const optionalAmount = () =>
  z
    .number({ error: 'Enter a number' })
    .refine(value => value !== 0, 'Enter an amount other than zero')
    .optional()

/** An ISO date, which is what DSDateField reads and writes whatever it displays. */
export const requiredDate = (message: string) =>
  z.string().trim().min(1, message).regex(ISO_DATE, 'Enter a valid date')

export const optionalDate = () =>
  z
    .string()
    .trim()
    .optional()
    .refine(value => !value || ISO_DATE.test(value), 'Enter a valid date')

/**
 * The tags applied to the asset. Not collected as a field — the tags step
 * writes it — but part of the schema so the wizard has one state object and one
 * verdict on it.
 *
 * `id` is the repository's, present only where the pair already exists there:
 * whatever creates the records links to that row, and a tag without one has to
 * be created before it can be linked. Optional rather than required because a
 * pair the user just typed genuinely has no id, and unvalidated beyond being a
 * string because it is resolved from the catalogue rather than entered.
 */
export const tagList = () =>
  z.array(
    z.object({
      id: z.string().optional(),
      tag_name: z.string().min(1),
      tag_value: z.string().min(1),
    }),
  )

/**
 * The values recorded on the valuation step, as rows of `asset_prices` in
 * waiting.
 *
 * Not collected as fields — the step writes the list — but part of the schema
 * so the wizard keeps one state object and one verdict on it, exactly as `tags`
 * does. An empty list is the honest answer for an optional step: nothing here
 * blocks a create.
 *
 * `close` is a number for the same reason an amount is, and `source` is checked
 * against the two a user may set — a connector's price is not something a
 * wizard writes.
 */
export const markList = () =>
  z.array(
    z.object({
      date: z.string().regex(ISO_DATE, 'Enter a valid date'),
      close: z.number(),
      source: z.enum([AssetPriceSource.MANUAL, AssetPriceSource.OVERRIDE]),
      fromPurchase: z.boolean().optional(),
    }),
  )

/**
 * The rows the cash-flows step writes — private_fund's own, the same reason
 * `markList` exists: not collected as fields, but part of the schema so the
 * wizard keeps one state object and one verdict on it. An empty list is the
 * honest answer for an optional step, same as `markList`'s.
 *
 * `amount` is unconstrained beyond being a number — a magnitude, not a signed
 * value; see `requiredAmount`'s note on why the sign is never checked here.
 */
export const cashFlowList = () =>
  z.array(
    z.object({
      investorIndex: z.number(),
      date: z.string().regex(ISO_DATE, 'Enter a valid date'),
      type: z.string().min(1),
      amount: z.number(),
    }),
  )

/*
 * The ownership step's shape. Nothing here is unconditionally required — which
 * of the three is owed depends on the mode — so the fields are declared loose
 * and `withOwnership` adds the rule.
 */
export const ownershipFields = () => ({
  [ACCOUNT_MODE_FIELD]: z
    .enum([WizardAccountMode.EXISTING, WizardAccountMode.NEW])
    .default(WizardAccountMode.EXISTING),
  ownerAccountId: optionalText(),
  ownerAccountName: optionalText(),
  ownerAccountType: optionalText(),
})

/**
 * The rule the mode implies: an existing account needs to be picked, a new one
 * needs a name and a type. Issues carry the field's path so each lands on the
 * control that is missing, rather than as one message about the step.
 *
 * Wraps the whole object because the rule spans fields; `superRefine` keeps the
 * schema's type, so a wizard's `z.infer` is unaffected.
 */
export const withOwnership = <T extends z.ZodObject<any>>(schema: T) =>
  schema.superRefine((values: WizardValues, ctx: z.core.$RefinementCtx) => {
    if (values[ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW) {
      if (!values.ownerAccountName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['ownerAccountName'],
          message: 'Name the account',
        })
      }
      if (!values.ownerAccountType?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['ownerAccountType'],
          message: 'Choose an ownership type',
        })
      }
      return
    }

    if (!values.ownerAccountId?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['ownerAccountId'],
        message: 'Pick the account this belongs to',
      })
    }
  })

/**
 * Whether one investor row is resolved: an existing account picked, or a new
 * one named and typed — the same either/or the ownership step's own mode
 * asks, just per row instead of once. Shared between the schema's own rule
 * below and the investing-entities step's inline feedback, so a row the
 * schema calls incomplete is never one the step draws as fine.
 */
export const isInvestorResolved = (investor: WizardInvestor) =>
  investor.accountMode === WizardAccountMode.NEW
    ? Boolean(investor.accountName?.trim() && investor.accountType?.trim())
    : Boolean(investor.accountId?.trim())

/**
 * The investing-entities step's shape: at least one investor, each carrying
 * the ownership step's own account fields plus a share of the commitment.
 * Loose here the same way `ownershipFields` is loose — nothing is required
 * unconditionally, because which of `accountId` or `accountName`/`accountType`
 * is owed depends on the row's own mode, which `withInvestors` is what checks.
 */
export const investorList = () =>
  z
    .array(
      z.object({
        accountMode: z.enum([WizardAccountMode.EXISTING, WizardAccountMode.NEW]),
        accountId: z.string(),
        accountName: z.string(),
        accountType: z.string(),
        share: z.number().optional(),
      }),
    )
    .min(1, 'Add at least one investor')

/**
 * The rule the split implies: every investor needs an account, exactly the
 * way `withOwnership` requires one, and together their shares have to add up
 * to the whole commitment — because that total is what decides how many
 * bundles `create()` writes and for how much each one, not a number this
 * wizard can leave to guesswork.
 *
 * Wraps the whole object because the rule spans `investors` and `amount`;
 * `superRefine` keeps the schema's type, so a wizard's `z.infer` is unaffected.
 */
export const withInvestors = <T extends z.ZodObject<any>>(schema: T) =>
  schema.superRefine((values: WizardValues, ctx: z.core.$RefinementCtx) => {
    const investors: WizardInvestor[] = values.investors ?? []

    investors.forEach((investor, index) => {
      if (!isInvestorResolved(investor)) {
        const path = investor.accountMode === WizardAccountMode.NEW ? 'accountName' : 'accountId'
        ctx.addIssue({
          code: 'custom',
          path: ['investors', index, path],
          message: investor.accountMode === WizardAccountMode.NEW
            ? 'Name the account'
            : 'Pick this investor’s account',
        })
      }

      if (!investor.share || investor.share <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['investors', index, 'share'],
          message: 'Enter this investor’s share',
        })
      }
    })

    const total = investors.reduce((sum, investor) => sum + (investor.share || 0), 0)
    if (investors.length && values.amount && Math.abs(total - values.amount) > 0.01) {
      ctx.addIssue({
        code: 'custom',
        path: ['investors'],
        message: 'Investor shares must add up to the full commitment',
      })
    }
  })

/**
 * The rule private_fund's own entities step implies — every row needs an
 * account, exactly the way `withInvestors` requires one, but nothing else:
 * there is no share to enter and no split to add up, because each row
 * becomes its own independent `add position` record for the full amount
 * every other row also gets. See PrivateFund/create.ts.
 *
 * private_security used to share this step too, each entity paying its own
 * price rather than holding the same amount — that turned out to want
 * `withInvestors`'s own split machinery instead (share, percent, the
 * allocation bar), so it moved there. See PrivateSecurity/schema.ts.
 */
export const withEntities = <T extends z.ZodObject<any>>(schema: T) =>
  schema.superRefine((values: WizardValues, ctx: z.core.$RefinementCtx) => {
    const investors: WizardInvestor[] = values.investors ?? []

    investors.forEach((investor, index) => {
      if (isInvestorResolved(investor)) return

      const path = investor.accountMode === WizardAccountMode.NEW ? 'accountName' : 'accountId'
      ctx.addIssue({
        code: 'custom',
        path: ['investors', index, path],
        message: investor.accountMode === WizardAccountMode.NEW
          ? 'Name the account'
          : 'Pick this entity’s account',
      })
    })
  })
